/**
 * <RubyToken> -- THE Ruby Token mark, rendered from the single hosted asset
 * (spec 2026-09-27 D10). Every gem in super-admin goes through here:
 * `RubyGemMark` and `RubyLoader` are thin callers of this component.
 *
 *  - `drawing` false -> the finished mark, static (beside amounts, logo use).
 *  - `drawing` true  -> loading / AI working. In brand ruby that is the
 *    glowing gem (`RubyGemGlow`: sparks firing along the facets, a local
 *    static asset shared with the POS till and platform-app).
 *
 * Brand ruby (the default) is a plain <img> of the hosted SVG: its own CSS
 * runs the cut loop and honours prefers-reduced-motion. It is a 12-88px
 * brand glyph, not content imagery, so it is not a ShimmerImage (a shimmer
 * would only add flicker); `data-ruby-token` marks it as the one exemption.
 *
 * RECOLOURED (`color="currentColor"` on a ruby button, the brand's on-red /
 * on-black finish) cannot be an <img> -- an image cannot take currentColor --
 * and it cannot be a CSS mask of the hosted file either, because a mask image
 * is fetched in CORS mode and the brand CDN sends no CORS headers (verified
 * 2026-09-27: the mask paints nothing). Those uses paint `RubyGemInline`,
 * which is the exact geometry the hosted file was generated from.
 *
 * FALLBACK. If the hosted file fails to load (CDN unreachable, or a stage
 * where it has not been uploaded yet) the inline drawing is painted instead,
 * so a loader never silently disappears.
 */
import React, { useState } from 'react';
import { rubyTokenUrl } from '../services/brandAssets';
import { RubyGemInline, RUBY_GEM_COLOR } from './RubyGemInline';
import { RubyGemGlow } from './RubyGemGlow';

export interface RubyTokenProps {
  /** Rendered size in px (square). */
  size?: number;
  /** Animated cut loop -- use while AI works / anything loads. */
  drawing?: boolean;
  /** Accessible name; omit for a decorative mark. */
  title?: string;
  /** Ink colour. Brand ruby by default; `currentColor` on coloured buttons. */
  color?: string;
  className?: string;
  style?: React.CSSProperties;
}

const isBrandInk = (c: string | undefined) =>
  !c || c.toLowerCase() === RUBY_GEM_COLOR.toLowerCase();

export const RubyToken: React.FC<RubyTokenProps> = ({
  size = 16, drawing = false, title, color, className, style,
}) => {
  const [failed, setFailed] = useState(false);

  // Loading in brand ruby = the glowing gem, a local static asset (not the CDN).
  if (drawing && (isBrandInk(color) || color?.toLowerCase() === '#dc143c')) {
    return <RubyGemGlow size={size} title={title} className={className} style={style} />;
  }

  if (failed || !isBrandInk(color)) {
    return (
      <RubyGemInline size={size} drawing={drawing} title={title} color={color ?? RUBY_GEM_COLOR} style={style} />
    );
  }

  return (
    <img
      src={rubyTokenUrl(size, drawing)}
      data-ruby-token={drawing ? 'drawing' : 'static'}
      width={size}
      height={size}
      alt={title || ''}
      aria-hidden={title ? undefined : true}
      draggable={false}
      onError={() => setFailed(true)}
      className={className}
      style={{ width: size, height: size, flexShrink: 0, display: 'inline-block', ...style }}
    />
  );
};

export default RubyToken;
